import { useUserContext } from "../../../../providers/UserProvider";
import { useProducts } from "../../../../hooks/useProducts/useProducts";
import { OpalRoleSuggestionInstanceEnablement } from "../../../components/OpalRoleSuggestionInstanceEnablement/OpalRoleSuggestionInstanceEnablement";
import { useEffect, useState } from "react";
import { emitToast } from "../../../../lib/toaster-utils";
import { useOrganizationSettings } from "../../../../hooks/useOrganizationSettings/useOrganizationSettings";
import { Attention, Typography } from "@optimizely/axiom";
import { GenerativeAIHipaaWarning } from "../../../components/GenerativeAISettingWarnings/GenerativeAIHipaaWarning";
import { GenerativeAIOrganizationSettingRequirement } from "../../../components/GenerativeAISettingWarnings/GenerativeAIOrganizationSettingRequirement";
import { useOrganization } from "../../../../hooks/useOrganization/useOrganization";
import { getErrorMessage } from "../../../../lib/utils";
import { datadogRum } from "@datadog/browser-rum";
import { Skeleton } from "../../../components/Skeleton/Skeleton";
import { OpalRoleSuggestionInstanceEnablementSkeleton } from "../../../components/OpalRoleSuggestionInstanceEnablement/OpalRoleSuggestionInstanceEnablementSkeleton";
import { UNEXPECTED_ERROR } from "../../../../constants/constants";
import { ProductInstance } from "../../../../domain/ProductInstance";
import { useOpalBranding } from "../../../../hooks/useOpalBranding/useOpalBranding";

export const OpalRoleSuggestionEnablementPage = () => {
    const { organizationId } = useUserContext();
    const { opalName } = useOpalBranding();
    const { organization } = useOrganization({ id: organizationId });
    const {
        instances = [],
        products = [],
        isLoading: productsLoading,
        updateInstance,
        error: failedGettingProducts
    } = useProducts({ organizationId });
    const { settings, loading: settingsLoading } = useOrganizationSettings({ organizationId });

    const [loading, setLoading] = useState(false);
    const [updatedInstances, setUpdatedInstances] = useState<ProductInstance[]>([]);
    const [error, setError] = useState("");
    const { healthCareDataEnabled: isHIPAAOrg } = organization || {};

    useEffect(() => {
        if (instances?.length && !updatedInstances?.length) {
            setUpdatedInstances(instances);
        }
    }, [instances, updatedInstances?.length]);

    const handleToggle = async ({ id }: { id: string }) => {
        setLoading(true);
        setError("");
        // `updateInstance` updates the SWR cache
        // so instead of creating new state and adding maintenance complexity to optimitstically track
        // and update instance state, we mutate the instances in the
        // hook.
        try {
            const toggledInstance = instances.find((instance) => instance.instanceId === id);
            const previousGenAIEnabledState = toggledInstance!.genAIEnabled;
            toggledInstance!.genAIEnabled = !previousGenAIEnabledState;

            await updateInstance({
                instance: {
                    ...toggledInstance,
                    name: toggledInstance!.instanceName,
                    id: toggledInstance!.instanceId,
                    nickname: toggledInstance!.nickname || ""
                },
                previousInstance: {
                    ...toggledInstance,
                    genAIEnabled: previousGenAIEnabledState,
                    name: toggledInstance!.instanceName,
                    id: toggledInstance!.instanceId,
                    nickname: toggledInstance!.nickname || ""
                }
            });
            emitToast({ message: `${opalName} settings successfully updated.` });
        } catch (error) {
            // revert the optimistic UI update
            const revertIndex = instances.findIndex((i) => i.instanceId === id);
            instances[revertIndex].genAIEnabled = !instances[revertIndex].genAIEnabled;
            const errorMessage = getErrorMessage(error);
            setError(errorMessage);
            datadogRum.addError(new Error("Failed to update GenAI Instance settings"), {
                error: errorMessage,
                organizationId: organizationId,
                component: "OpalRoleSuggestionEnablementPage",
                function: "handleDebouncedUpdates"
            });
        } finally {
            setLoading(false);
        }
    };

    const genAIEnabled = settings?.genAI?.enabled;
    const showOptions = genAIEnabled && !isHIPAAOrg;

    return (
        <div className="push-double content-container">
            <Typography type="header1" tag="h1">
                Enable {opalName} for Products
            </Typography>
            <Typography type="body" tag="p" className="soft-triple--bottom">
                When {opalName} is enabled for an instance, granting a user permissions to that instance will also
                prompt you to assign {opalName} permissions if the user does not already have {opalName} entitlements.
            </Typography>
            {settingsLoading && <Skeleton width="100%" />}
            {!(settingsLoading || genAIEnabled) && <GenerativeAIOrganizationSettingRequirement />}
            {genAIEnabled && isHIPAAOrg && <GenerativeAIHipaaWarning />}
            {!!(error || failedGettingProducts) && (
                <Attention type="bad-news" className="push-double--bottom">
                    {error || UNEXPECTED_ERROR}
                </Attention>
            )}

            {showOptions && productsLoading && <OpalRoleSuggestionInstanceEnablementSkeleton />}
            {showOptions && !productsLoading && instances.length > 0 && (
                <OpalRoleSuggestionInstanceEnablement
                    disabled={loading}
                    instances={instances}
                    onSelect={handleToggle}
                    products={products}
                />
            )}
        </div>
    );
};
